import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Burger,
  Drawer,
  Group,
  NavLink as MantineNavLink,
  Menu,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconCalendarHeart,
  IconChevronDown,
  IconHeartHandshake,
  IconMail,
  IconPhotoHeart,
  IconSparkles,
  IconToolsKitchen2,
  IconUmbrella,
} from "@tabler/icons-react";

type StayItem = { key: string; labelKey: string; icon: React.ReactNode };

const useNavItems = () => {
  const { t } = useTranslation();
  const stayItems: StayItem[] = [
    {
      key: "food",
      labelKey: "menu.stay.food",
      icon: <IconToolsKitchen2 size={18} />,
    },
    {
      key: "guests",
      labelKey: "menu.stay.guests",
      icon: <IconUmbrella size={18} />,
    },
  ];

  const primaryItems = [
    {
      key: "book",
      label: t("menu.book"),
      icon: <IconCalendarHeart size={18} />,
    },
    {
      key: "gallery",
      label: t("menu.gallery"),
      icon: <IconPhotoHeart size={18} />,
    },
    {
      key: "contact",
      label: t("menu.contact"),
      icon: <IconMail size={18} />,
    },
  ];

  return { primaryItems, stayItems, t };
};

const isStayPath = (pathname: string) =>
  pathname.startsWith("/food") || pathname.startsWith("/guests");

const DesktopNav: React.FC = () => {
  const { primaryItems, stayItems, t } = useNavItems();
  const navigate = useNavigate();
  const location = useLocation();

  const linkActive = (key: string) => location.pathname === `/${key}`;
  const stayActive = isStayPath(location.pathname);

  return (
    <Group gap={4} visibleFrom="sm">
      {primaryItems.slice(0, 1).map((item) => (
        <UnstyledButton
          key={item.key}
          onClick={() => navigate(`/${item.key}`)}
          px="md"
          py={8}
          style={{
            borderRadius: 999,
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: linkActive(item.key)
              ? "var(--mantine-color-sea-1)"
              : "transparent",
            color: linkActive(item.key)
              ? "var(--mantine-color-sea-7)"
              : "var(--mantine-color-text)",
            fontWeight: 600,
            fontSize: "0.95rem",
          }}
        >
          {item.icon}
          {item.label}
        </UnstyledButton>
      ))}

      <Menu
        trigger="hover"
        openDelay={50}
        closeDelay={150}
        position="bottom-start"
        withArrow
      >
        <Menu.Target>
          <UnstyledButton
            px="md"
            py={8}
            style={{
              borderRadius: 999,
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: stayActive
                ? "var(--mantine-color-sea-1)"
                : "transparent",
              color: stayActive
                ? "var(--mantine-color-sea-7)"
                : "var(--mantine-color-text)",
              fontWeight: 600,
              fontSize: "0.95rem",
            }}
          >
            <IconHeartHandshake size={18} />
            {t("menu.stay.stay")}
            <IconChevronDown size={14} />
          </UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown>
          {stayItems.map((item) => (
            <Menu.Item
              key={item.key}
              leftSection={item.icon}
              onClick={() => navigate(`/${item.key}`)}
            >
              {t(item.labelKey)}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>

      {primaryItems.slice(1).map((item) => (
        <UnstyledButton
          key={item.key}
          onClick={() => navigate(`/${item.key}`)}
          px="md"
          py={8}
          style={{
            borderRadius: 999,
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: linkActive(item.key)
              ? "var(--mantine-color-sea-1)"
              : "transparent",
            color: linkActive(item.key)
              ? "var(--mantine-color-sea-7)"
              : "var(--mantine-color-text)",
            fontWeight: 600,
            fontSize: "0.95rem",
          }}
        >
          {item.icon}
          {item.label}
        </UnstyledButton>
      ))}
    </Group>
  );
};

const MobileNav: React.FC = () => {
  const [opened, { toggle, close }] = useDisclosure(false);
  const { primaryItems, stayItems, t } = useNavItems();
  const navigate = useNavigate();

  const go = (path: string) => {
    navigate(path);
    close();
  };

  return (
    <>
      <Burger
        opened={opened}
        onClick={toggle}
        hiddenFrom="sm"
        aria-label="Toggle navigation"
      />
      <Drawer
        opened={opened}
        onClose={close}
        position="right"
        size="80%"
        padding="lg"
        title={
          <Group gap="xs">
            <IconSparkles size={20} color="var(--mantine-color-blossom-5)" />
            <Text ff='"Fraunces", serif' fw={600} fz="xl">
              Villa Braou
            </Text>
          </Group>
        }
      >
        <Stack gap={4} mt="md">
          {primaryItems.slice(0, 1).map((item) => (
            <MantineNavLink
              key={item.key}
              label={item.label}
              leftSection={item.icon}
              onClick={() => go(`/${item.key}`)}
              variant="filled"
            />
          ))}
          <MantineNavLink
            label={t("menu.stay.stay")}
            leftSection={<IconHeartHandshake size={18} />}
            defaultOpened
            childrenOffset={28}
          >
            {stayItems.map((item) => (
              <MantineNavLink
                key={item.key}
                label={t(item.labelKey)}
                leftSection={item.icon}
                onClick={() => go(`/${item.key}`)}
              />
            ))}
          </MantineNavLink>
          {primaryItems.slice(1).map((item) => (
            <MantineNavLink
              key={item.key}
              label={item.label}
              leftSection={item.icon}
              onClick={() => go(`/${item.key}`)}
            />
          ))}
        </Stack>
      </Drawer>
    </>
  );
};

const BraouMenu: React.FC = () => {
  return (
    <Group gap="xs" wrap="nowrap">
      <DesktopNav />
      <MobileNav />
    </Group>
  );
};

export default BraouMenu;
